// src/components/artwork/tabs/ArtworkSpecificationsTab.jsx
import React from "react";
import { useTranslation } from "react-i18next";

const toNumberOrEmpty = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return value;
};

const normalizeSpecs = (source = {}) => {
  const specs =
    source.specifications && typeof source.specifications === "object"
      ? source.specifications
      : {};

  const dimensions = specs.dimensions || {};
  const framedSize = specs.framedSize || {};
  const paperSize = specs.paperSize || {};

  const unit = source.dimensions_unit || source.unit || dimensions.unit || "cm";

  return {
    ...specs,
    dimensions: {
      unit,
      height: source.dimensions_height ?? source.height ?? dimensions.height ?? "",
      width: source.dimensions_width ?? source.width ?? dimensions.width ?? "",
      depth: source.dimensions_depth ?? source.depth ?? dimensions.depth ?? "",
    },
    framedSize: {
      unit: framedSize.unit || source.framed_unit || unit || "cm",
      height: framedSize.height ?? source.framed_height ?? "",
      width: framedSize.width ?? source.framed_width ?? "",
      depth: framedSize.depth ?? source.framed_depth ?? "",
    },
    paperSize: {
      unit: paperSize.unit || source.paper_unit || unit || "cm",
      height: paperSize.height ?? source.paper_height ?? "",
      width: paperSize.width ?? source.paper_width ?? "",
    },
    duration: specs.duration ?? source.duration ?? "",
  };
};

const toDbNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const formatDimensionsString = (dimensions = {}) => {
  const unit = dimensions.unit || "cm";
  const values = [dimensions.height, dimensions.width, dimensions.depth]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => String(value).trim())
    .filter(Boolean);

  return values.length ? `${values.join(" x ")} ${unit}` : "";
};

const formatDimensionValue = (value, unit) => {
  if (value === "" || value === null || value === undefined) return "—";
  return `${value}${unit ? ` ${unit}` : ""}`;
};

const Field = ({ label, value, onChange, readOnly, unit }) => {
  if (readOnly) {
    return (
      <div className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
        <p className="min-h-[38px] rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          {formatDimensionValue(value, unit)}
        </p>
      </div>
    );
  }

  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="number"
          step="0.01"
          value={toNumberOrEmpty(value)}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
          placeholder="0"
        />
        {unit && <span className="min-w-8 text-sm text-gray-500">{unit}</span>}
      </div>
    </label>
  );
};

const ReadOnlyValue = ({ label, value }) => (
  <div className="block">
    <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
    <p className="min-h-[38px] rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
      {value || "—"}
    </p>
  </div>
);

const SwapButton = ({ onClick, disabled, title, label }) => {
  if (disabled) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-black"
      title={title}
    >
      ⇄ {label}
    </button>
  );
};

const Section = ({ title, action, hint, children }) => (
  <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
    <div className="mb-3 flex items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-gray-500 leading-relaxed">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const UnitSelect = ({ label, value, onChange, readOnly, inchesLabel = "inches" }) => {
  if (readOnly) {
    return <ReadOnlyValue label={label} value={value || "cm"} />;
  }

  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      <select
        value={value || "cm"}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
      >
        <option value="cm">cm</option>
        <option value="mm">mm</option>
        <option value="inches">{inchesLabel}</option>
      </select>
    </label>
  );
};

const ArtworkSpecificationsTab = ({
  formData,
  setFormData,
  readOnly = true,
  isEditing = false,
  canEdit: canEditProp,
  artwork,
  t: tProp,
}) => {
  const { t: i18nT } = useTranslation();
  const t = tProp || i18nT;
  const data = formData || artwork || {};
  const specs = normalizeSpecs(data);

  // Règle attendue :
  // - lecture fiche : pas de modification
  // - mode édition : modification autorisée
  // Compatibilité :
  // - nouveau parent : isEditing={true}
  // - ancien parent : readOnly={false}
  const canEdit = canEditProp !== undefined ? !!canEditProp : (isEditing === true || readOnly === false);
  const isReadOnly = !canEdit;

  const updateSpecs = (nextSpecs) => {
    if (!setFormData || isReadOnly) return;

    // IMPORTANT : normaliser uniquement depuis nextSpecs (sans les colonnes plates de data)
    // pour éviter que les anciennes valeurs de formData écrasent les nouvelles.
    const normalizedNextSpecs = normalizeSpecs({ specifications: nextSpecs });

    const nextDimensions = normalizedNextSpecs.dimensions || {};
    const nextFramed = normalizedNextSpecs.framedSize || {};
    const nextPaper = normalizedNextSpecs.paperSize || {};

    setFormData((prev) => {
      const previous = prev || data || {};
      const previousSpecs =
        previous.specifications && typeof previous.specifications === "object"
          ? previous.specifications
          : {};

      return {
        ...previous,
        specifications: {
          ...previousSpecs,
          ...normalizedNextSpecs,
        },
        dimensions: formatDimensionsString(nextDimensions),

        // Colonnes SQL plates principales
        dimensions_unit: nextDimensions.unit || "cm",
        dimensions_height: toDbNumber(nextDimensions.height),
        dimensions_width: toDbNumber(nextDimensions.width),
        dimensions_depth: toDbNumber(nextDimensions.depth),
        height: toDbNumber(nextDimensions.height),
        width: toDbNumber(nextDimensions.width),
        depth: toDbNumber(nextDimensions.depth),
        unit: nextDimensions.unit || "cm",

        // framed_unit n'existe PAS dans artworks — dans specifications JSONB uniquement
        framed_height: toDbNumber(nextFramed.height),
        framed_width: toDbNumber(nextFramed.width),
        framed_depth: toDbNumber(nextFramed.depth),

        // paper_unit n'existe PAS dans artworks — dans specifications JSONB uniquement
        paper_height: toDbNumber(nextPaper.height),
        paper_width: toDbNumber(nextPaper.width),

        duration: normalizedNextSpecs.duration || "",
      };
    });
  };

  // Deep copy de specs pour éviter les références partagées sur les objets imbriqués
  const deepCopySpecs = () => ({
    ...specs,
    dimensions: { ...specs.dimensions },
    framedSize: { ...specs.framedSize },
    paperSize: { ...specs.paperSize },
  });

  const updateNested = (section, key, value) => {
    const nextSpecs = deepCopySpecs();
    nextSpecs[section] = { ...(nextSpecs[section] || {}), [key]: value };
    updateSpecs(nextSpecs);
  };

  const updateUnit = (unit) => {
    const nextSpecs = deepCopySpecs();
    nextSpecs.dimensions = { ...nextSpecs.dimensions, unit };
    // Ne pas écraser l'unité encadrée/papier si déjà définie
    nextSpecs.framedSize = { ...nextSpecs.framedSize, unit: nextSpecs.framedSize.unit || unit };
    nextSpecs.paperSize = { ...nextSpecs.paperSize, unit: nextSpecs.paperSize.unit || unit };
    updateSpecs(nextSpecs);
  };

  const swapHeightWidth = (section) => {
    const nextSpecs = deepCopySpecs();
    const current = { ...nextSpecs[section] };
    nextSpecs[section] = { ...current, height: current.width ?? "", width: current.height ?? "" };
    updateSpecs(nextSpecs);
  };

  return (
    <div className="space-y-4">
      <Section
        title={t("artworkSpecifications.sections.dimensions")}
        hint={t("artworkSpecifications.hints.dimensions", {
          defaultValue: "L’œuvre seule, sans cadre ni marges (la « vue »).",
        })}
        action={
          <SwapButton
            disabled={isReadOnly}
            onClick={() => swapHeightWidth("dimensions")}
            title={t("artworkSpecifications.swapTitle")}
            label={t("artworkSpecifications.swap")}
          />
        }
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Field
            label={t("artworkSpecifications.fields.height")}
            value={specs.dimensions.height}
            unit={specs.dimensions.unit}
            readOnly={isReadOnly}
            onChange={(value) => updateNested("dimensions", "height", value)}
          />
          <Field
            label={t("artworkSpecifications.fields.width")}
            value={specs.dimensions.width}
            unit={specs.dimensions.unit}
            readOnly={isReadOnly}
            onChange={(value) => updateNested("dimensions", "width", value)}
          />
          <Field
            label={t("artworkSpecifications.fields.depth")}
            value={specs.dimensions.depth}
            unit={specs.dimensions.unit}
            readOnly={isReadOnly}
            onChange={(value) => updateNested("dimensions", "depth", value)}
          />
          <UnitSelect
            label={t("artworkSpecifications.fields.unit")}
            value={specs.dimensions.unit || "cm"}
            readOnly={isReadOnly}
            onChange={updateUnit}
            inchesLabel={t("artworkSpecifications.units.inches")}
          />
        </div>
      </Section>

      <Section
        title={t("artworkSpecifications.sections.framed")}
        hint={t("artworkSpecifications.hints.framed", {
          defaultValue: "Hors-tout, cadre compris — dimensions utiles pour l’accrochage, le transport et l’assurance.",
        })}
        action={
          <SwapButton
            disabled={isReadOnly}
            onClick={() => swapHeightWidth("framedSize")}
            title={t("artworkSpecifications.swapTitle")}
            label={t("artworkSpecifications.swap")}
          />
        }
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Field
            label={t("artworkSpecifications.fields.framedHeight")}
            value={specs.framedSize.height}
            unit={specs.framedSize.unit || specs.dimensions.unit}
            readOnly={isReadOnly}
            onChange={(value) => updateNested("framedSize", "height", value)}
          />
          <Field
            label={t("artworkSpecifications.fields.framedWidth")}
            value={specs.framedSize.width}
            unit={specs.framedSize.unit || specs.dimensions.unit}
            readOnly={isReadOnly}
            onChange={(value) => updateNested("framedSize", "width", value)}
          />
          <Field
            label={t("artworkSpecifications.fields.framedDepth")}
            value={specs.framedSize.depth}
            unit={specs.framedSize.unit || specs.dimensions.unit}
            readOnly={isReadOnly}
            onChange={(value) => updateNested("framedSize", "depth", value)}
          />
          <UnitSelect
            label={t("artworkSpecifications.fields.frameUnit")}
            value={specs.framedSize.unit || specs.dimensions.unit || "cm"}
            readOnly={isReadOnly}
            onChange={(value) => updateNested("framedSize", "unit", value)}
            inchesLabel={t("artworkSpecifications.units.inches")}
          />
        </div>
      </Section>

      <Section
        title={t("artworkSpecifications.sections.paper")}
        hint={t("artworkSpecifications.hints.paper", {
          defaultValue: "Œuvres sur papier : dimensions de la feuille entière, souvent plus grandes que l’image.",
        })}
        action={
          <SwapButton
            disabled={isReadOnly}
            onClick={() => swapHeightWidth("paperSize")}
            title={t("artworkSpecifications.swapTitle")}
            label={t("artworkSpecifications.swap")}
          />
        }
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field
            label={t("artworkSpecifications.fields.paperHeight")}
            value={specs.paperSize.height}
            unit={specs.paperSize.unit || specs.dimensions.unit}
            readOnly={isReadOnly}
            onChange={(value) => updateNested("paperSize", "height", value)}
          />
          <Field
            label={t("artworkSpecifications.fields.paperWidth")}
            value={specs.paperSize.width}
            unit={specs.paperSize.unit || specs.dimensions.unit}
            readOnly={isReadOnly}
            onChange={(value) => updateNested("paperSize", "width", value)}
          />
          <UnitSelect
            label={t("artworkSpecifications.fields.paperUnit")}
            value={specs.paperSize.unit || specs.dimensions.unit || "cm"}
            readOnly={isReadOnly}
            onChange={(value) => updateNested("paperSize", "unit", value)}
            inchesLabel={t("artworkSpecifications.units.inches")}
          />
        </div>
      </Section>

      <Section title={t("artworkSpecifications.sections.duration")}>
        {isReadOnly ? (
          <p className="min-h-[38px] rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-700">
            {specs.duration || "—"}
          </p>
        ) : (
          <input
            type="text"
            value={specs.duration || ""}
            onChange={(e) => updateSpecs({ ...specs, duration: e.target.value })}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
            placeholder={t("artworkSpecifications.durationPlaceholder")}
          />
        )}
      </Section>
    </div>
  );
};

export default ArtworkSpecificationsTab;